// MCIP-22 — Incident-response screen (design board surface 3).
// Persona question, stated per board rule: "what do I report to the backend
// team, right now?" Left: the filterable incident-group list (24h/72h/7d —
// the API clamps at 168h; longer horizons belong to the trends screen).
// Right: the selected group — verbatim evidence, affected jobs as deep links,
// the copy-only routing card (D2: a human posts, the platform never does),
// and the async Glean draft. A manager/developer toggle switches between
// summary-and-ownership and evidence-and-prompt flavors.
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { pollAnalysis } from '../../lib/aiPoll';
import {
  totalsLabel, INCIDENT_WINDOWS, clampWindowChip, relTime, jobViewLink,
  jobRows, crossPodNote,
} from '../../lib/incidents';
import SharedTopBar from '../shared/TopBar';
import PillGroup from '../shared/PillGroup';
import CopyButton from '../shared/CopyButton';

function paramWindow() {
  return clampWindowChip(new URLSearchParams(window.location.search).get('window'));
}

/** Routing card v2 — evidence-based (Aug 11 review): every staging finding
 *  starts at the staging channel; infra-dependency evidence adds the
 *  platform-support escalation; team channels appear ONLY when verified —
 *  pattern-derived guesses (and their scary warning) are gone. Copy-only (D2). */
function RoutingCard({ routing, pod }) {
  if (!routing) return null;
  const lines = [
    { label: 'Report in', value: routing.primary },
    routing.infra ? { label: 'Escalate infra', value: routing.infra } : null,
    routing.team ? { label: 'Team (verified)', value: [routing.team, routing.team_tag].filter(Boolean).join(' · ') } : null,
    { label: 'On-call', value: routing.oncall },
  ].filter(Boolean);
  const copyText = lines.map((l) => `${l.label}: ${l.value}`).join('\n');
  return (
    <div className="incident-route">
      <h3 className="detail__h3">Where to report — copy-only, never auto-posted</h3>
      <p className="card__subtitle">{routing.reason}</p>
      <dl className="incident-route__lines">
        {lines.map((l) => (
          <div key={l.label} className="incident-route__row">
            <dt>{l.label}</dt>
            <dd><code>{l.value}</code></dd>
          </div>
        ))}
      </dl>
      <CopyButton text={copyText} label="Copy route" />
    </div>
  );
}

/** The async Glean draft — same 202/poll contract as the fix card's analysis.
 *  Every call carries the screen's window: the server must find the group in
 *  the SAME view the user is looking at, not its narrower default. */
function DraftSection({ group, windowHours }) {
  const [state, setState] = useState('idle'); // idle | loading | done | error
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);

  async function run() {
    setState('loading'); setError(null);
    try {
      const force = state === 'done';
      const result = await pollAnalysis({
        start: () => api.incidentDraft(group.pod, { force, windowHours }),
        probe: () => api.incidentDraftStatus(group.pod, { windowHours }),
        fetchResult: () => api.incidentDraft(group.pod, { windowHours }),
      });
      setDraft(result); setState('done');
    } catch (e) {
      setError(e); setState('error');
    }
  }

  return (
    <div className="incident-draft-section">
      <div className="prompt__head">
        <h3 className="detail__h3">AI incident draft — "drafted by CI Intelligence, verify before posting"</h3>
        <button type="button" className="primary-btn" disabled={state === 'loading'} onClick={run}>
          {state === 'loading' ? 'Drafting… (1–2 min)' : state === 'done' ? 'Re-draft' : 'Draft report'}
        </button>
      </div>
      {state === 'loading' && (
        <p className="ai__note"><span className="ai-spin" aria-hidden="true" /> Glean is drafting from the evidence…</p>
      )}
      {state === 'error' && (
        <p className={`ai__note ${error?.status === 503 ? '' : 'ai__note--error'}`}>
          {error?.status === 503
            ? 'AI drafting is not configured — set GLEAN_TOKEN on the API (Glean admin → Client API Tokens).'
            : `Draft failed: ${error?.message}`}
        </p>
      )}
      {state === 'done' && draft && (
        <div className="incident-draft">
          <div className="prompt__head">
            <h4 className="detail__h3">Thread title</h4>
            <CopyButton text={draft.thread_title || ''} label="Copy title" />
          </div>
          <pre className="incident-draft__title">{draft.thread_title}</pre>
          <div className="prompt__head">
            <h4 className="detail__h3">Thread description</h4>
            <CopyButton text={draft.thread_description || ''} label="Copy description" />
          </div>
          <pre className="ai__text">{draft.thread_description}</pre>
          {draft.citations?.length > 0 && (
            <ul className="ai__citations">
              {draft.citations.map((c) => (
                <li key={c.url}><a href={c.url} target="_blank" rel="noreferrer">{c.title}</a></li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function IncidentsScreen({
  regions, region, onRegion, environments, environment, onEnvironment,
  mode, onMode, topbarNav,
}) {
  const [windowHours, setWindowHours] = useState(paramWindow);
  const [incidents, setIncidents] = useState(null);
  const [selectedPod, setSelectedPod] = useState(() => new URLSearchParams(window.location.search).get('pod') || null);
  const [flavor, setFlavor] = useState('manager'); // manager | developer
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const fetchSeq = useRef(0);

  // ?window= and ?pod= keep the view deep-linkable (Slack cites exact state).
  useEffect(() => {
    const url = new URL(window.location.href);
    if (windowHours === 72) url.searchParams.delete('window');
    else url.searchParams.set('window', String(windowHours));
    if (selectedPod) url.searchParams.set('pod', selectedPod);
    else url.searchParams.delete('pod');
    window.history.replaceState({}, '', url);
  }, [windowHours, selectedPod]);

  useEffect(() => {
    const seq = ++fetchSeq.current;
    setError(null);
    setStale(true);
    api.incidents({ windowHours, region, environment })
      .then((r) => {
        if (seq !== fetchSeq.current) return;
        setIncidents(r);
        // Keep the selection when the pod survives the new window; else first group.
        setSelectedPod((prev) => (r.groups.some((g) => g.pod === prev) ? prev : (r.groups[0]?.pod || null)));
      })
      .catch((e) => { if (seq === fetchSeq.current) setError(e.message); })
      .finally(() => { if (seq === fetchSeq.current) setStale(false); });
  }, [windowHours, region, environment, retryTick]);

  const selected = incidents?.groups.find((g) => g.pod === selectedPod) || null;

  return (
    <div className="app">
      <SharedTopBar
        regions={regions} region={region} onRegion={onRegion}
        environments={environments} environment={environment} onEnvironment={onEnvironment}
        asOf={incidents?.generated_at} refreshing={stale}
        onRefresh={() => setRetryTick((t) => t + 1)}
        mode={mode} onMode={onMode}
        nav={topbarNav}
      >
        <label className="field">
          <span>Window</span>
          <PillGroup
            label="Incident window"
            options={INCIDENT_WINDOWS.map((w) => String(w.hours))}
            value={String(windowHours)}
            onChange={(v) => setWindowHours(Number(v))}
            format={(v) => INCIDENT_WINDOWS.find((w) => w.hours === Number(v)).label}
          />
        </label>
      </SharedTopBar>

      <main className={`page${stale ? ' is-stale' : ''}`}>
        <p className="context">
          Incident response · SERVICE_5XX + GRPC_BREAK clusters in the last{' '}
          {INCIDENT_WINDOWS.find((w) => w.hours === windowHours).label} · what do I report to the backend team, right now?
        </p>

        {error && (
          <p className="notice notice--error">
            Couldn't load incidents: {error}
            {' '}
            <button type="button" className="ghost-btn" onClick={() => setRetryTick((t) => t + 1)}>Retry</button>
          </p>
        )}

        {!incidents && !error && <p className="notice">Loading incidents…</p>}

        {incidents && incidents.groups.length === 0 && (
          <section className="card empty">
            <h2 className="card__title">No backend incidents in this window</h2>
            <p className="card__subtitle">
              No {incidents.categories.join(' / ')} cluster was last seen in the past{' '}
              {INCIDENT_WINDOWS.find((w) => w.hours === windowHours).label}. Widen the window above —
              past-week archaeology beyond 7d belongs to the trends screen.
            </p>
          </section>
        )}

        {incidents && incidents.groups.length > 0 && (
          <div className="incidents-layout">
            <div className="incidents-list">
              {incidents.groups.map((g) => (
                <button
                  key={g.pod}
                  type="button"
                  className={`card incident-item${g.pod === selectedPod ? ' is-selected' : ''} card--alert`}
                  onClick={() => setSelectedPod(g.pod)}
                  aria-pressed={g.pod === selectedPod}
                >
                  <span className="incident-item__head">
                    <b>{g.pod}</b>
                    {[...new Set(g.clusters.map((c) => c.failure_category))].map((cat) => (
                      <span key={cat} className="tag tag--bug">{cat}</span>
                    ))}
                  </span>
                  <span className="muted">{totalsLabel(g.totals)} · {relTime(g.last_activity_at)}</span>
                </button>
              ))}
            </div>

            {selected && (
              <section className="card incidents-detail">
                <header className="card__head">
                  <div>
                    <h2 className="card__title">{selected.pod} — backend incidents</h2>
                    <p className="card__subtitle">
                      {totalsLabel(selected.totals)} · last activity {relTime(selected.last_activity_at)}
                    </p>
                    {/* Origin rule, spelled out: no job from another pod may
                        appear here without the reader knowing why. */}
                    {crossPodNote(selected) && (
                      <p className="card__subtitle incident-origin-note">{crossPodNote(selected)}</p>
                    )}
                  </div>
                  {/* Manager: summary and ownership. Developer: evidence and prompt. */}
                  <PillGroup
                    label="Detail flavor"
                    options={['manager', 'developer']}
                    value={flavor}
                    onChange={setFlavor}
                  />
                </header>

                {flavor === 'developer' && (
                  <>
                    <h3 className="detail__h3">Evidence — verbatim cluster signals</h3>
                    {selected.clusters.map((c) => (
                      <div key={c.cluster_id} className="incident-evidence">
                        {/* Monospace = un-edited truth: the title carries the
                            framework's marker text (queue names, services). */}
                        <p className="evidence-line"><code>{c.title}</code></p>
                        <p className="muted incident-evidence__meta">
                          <span className="tag tag--bug">{c.failure_category}</span>
                          {c.origin_pod && c.origin_pod !== selected.pod && (
                            <span className="tag tag--origin" title="originates in another pod's jobs">origin: {c.origin_pod}</span>
                          )}
                          {c.primary_service && <> · service {c.primary_service}</>}
                          {' '}· {c.member_count} failure{c.member_count === 1 ? '' : 's'}
                          {' '}· seen {relTime(c.first_seen_at)} → {relTime(c.last_seen_at)}
                          {' '}·{' '}
                          {jobRows(c).map((j, i) => (
                            <span key={j.job_id}>
                              {i > 0 && ', '}
                              <a className="run-link" href={jobViewLink(j.job_id, c.cluster_id)}>{j.job_id} ↗</a>
                              {Number.isFinite(j.failures) && !j.pooled && <> ({j.failures})</>}
                              {j.origin && <span className="tag tag--origin">origin</span>}
                            </span>
                          ))}
                        </p>
                      </div>
                    ))}
                  </>
                )}

                {flavor === 'manager' && (
                  <>
                    <h3 className="detail__h3">Affected jobs</h3>
                    <div className="card__table">
                      <table className="data-table">
                        <thead>
                          <tr><th>job</th><th>signal</th><th className="num">failures</th><th>last seen</th></tr>
                        </thead>
                        <tbody>
                          {/* One row per WITNESS JOB with that job's own count
                              — the pooled cluster total repeated on every row
                              read like double-counting. Origin = the job that
                              saw the fingerprint first; a pod tag calls out
                              rows witnessed from outside this group's pod
                              (pure-owner lens only). A cluster whose members
                              no longer resolve to ANY job (data migration)
                              renders one placeholder row — the incident must
                              never vanish from the table. */}
                          {selected.clusters.flatMap((c) => {
                            const signalCell = (
                              <td className="mono-cell">
                                <code>{c.title}</code>
                                {c.dd_trace_url && (
                                  <> <a className="run-link" href={c.dd_trace_url} target="_blank" rel="noreferrer">DD trace ↗</a></>
                                )}
                                {c.category_label && <span className="tag" title={c.failure_category}>{c.category_label}</span>}
                                {c.origin_pod && c.origin_pod !== selected.pod && (
                                  <span className="tag tag--origin" title="this incident originates in another pod's jobs; shown here because this pod is affected">origin: {c.origin_pod}</span>
                                )}
                              </td>
                            );
                            const rows = jobRows(c);
                            if (!rows.length) {
                              // Owner lens (fault owned here, witnessed by other
                              // pods' jobs — see the note) vs the data-migration
                              // edge (members no longer resolve to any job).
                              const witnessed = (c.spread || []).map((s) => s.pod).join(', ');
                              return [(
                                <tr key={`${c.cluster_id}-nojob`}>
                                  <td className="muted" title={witnessed
                                    ? `owned by ${selected.pod}; witnessed by ${witnessed} — job rows live in those pods' views`
                                    : 'cluster members no longer resolve to a job (data migration) — the evidence still stands'}>
                                    {witnessed ? `witnessed by ${witnessed}` : 'no resolvable job'}
                                  </td>
                                  {signalCell}
                                  <td className="num">{c.member_count}</td>
                                  <td>{relTime(c.last_seen_at)}</td>
                                </tr>
                              )];
                            }
                            return rows.map((j) => (
                              <tr key={`${c.cluster_id}-${j.job_id}`}>
                                <td>
                                  <a className="run-link" href={jobViewLink(j.job_id, c.cluster_id)}>{j.job_id} ↗</a>
                                  {j.origin && <span className="tag tag--origin" title="first job to see this signal">origin</span>}
                                  {j.pod && j.pod !== selected.pod && (
                                    <span className="tag" title={`witnessed by the ${j.pod} pod's job`}>{j.pod}</span>
                                  )}
                                </td>
                                {signalCell}
                                <td className="num" title={j.pooled ? 'cluster total (per-job split unavailable)' : `this job's share of ${c.member_count}`}>
                                  {j.failures ?? c.member_count}
                                </td>
                                <td>{relTime(j.last_seen_at || c.last_seen_at)}</td>
                              </tr>
                            ));
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {/* Deterministic, human-language report text (no Glean, no
                    cost): what broke, where, with the concrete trace. */}
                {selected.thread_title && (
                  <div className="incident-headline">
                    <h3 className="detail__h3">Report text — deterministic, copy-ready</h3>
                    <p className="incident-headline__title"><b>{selected.thread_title}</b></p>
                    <pre className="incident-headline__desc">{selected.thread_description}</pre>
                    <CopyButton
                      text={`${selected.thread_title}\n\n${selected.thread_description}`}
                      label="Copy report text"
                    />
                  </div>
                )}
                <RoutingCard routing={selected.routing_v2 || selected.routing} pod={selected.pod} />
                <DraftSection group={selected} windowHours={windowHours} />
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
